/**
 * Centralized issue marker visual configuration.
 *
 * Provides priority-to-color and type-to-icon mapping with support for
 * API configuration overrides via {@link setConfiguration}.
 *
 * Usage:
 * 1. Call {@link setConfiguration} once when the configuration query resolves.
 * 2. Call {@link getMarkerColor} / {@link getMarkerTypeIcon} anywhere — they
 *    automatically use the stored configuration or fall back to defaults.
 */

import { createSignal } from 'solid-js';
import type {
  ConfigurationDetails,
  GetConfigurationResponse,
} from '@data-access/connect/tc-issue-management';

import { getLogger } from '@trimble/logging';
import issueIconUrl from '../assets/issueIcons/issue.svg';
import faultIconUrl from '../assets/issueIcons/fault.svg';
import commentIconUrl from '../assets/issueIcons/comment.svg';
import requestIconUrl from '../assets/issueIcons/request.svg';
import inquiryIconUrl from '../assets/issueIcons/inquiry.svg';
import solutionIconUrl from '../assets/issueIcons/solution.svg';
import remarkIconUrl from '../assets/issueIcons/remark.svg';
import clashIconUrl from '../assets/issueIcons/clash.svg';
import bcfIconUrl from '../assets/bcf.svg';

/** BCF marker SVG asset URL (monochrome fallback icon) */
export const BcfMarkerIcon = bcfIconUrl;

// ---------------------------------------------------------------------------
// Module-level configuration state
// ---------------------------------------------------------------------------

let configuration: GetConfigurationResponse | null = null;
let resolvedIconsMap: Map<string, string> = new Map();
const iconPathCache = new Map<string, string>();

const [configReady, setConfigReady] = createSignal(false);
const [iconsVersion, setIconsVersion] = createSignal(0);

const logger = getLogger();
// ---------------------------------------------------------------------------
// Default mappings
// ---------------------------------------------------------------------------

const DEFAULT_PRIORITY_COLORS: Record<string, string> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Critical: '#D52A33',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  High: '#FFA500',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Normal: '#6A6976',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Low: '#6A6976',
};

const FALLBACK_PRIORITY_COLOR = '#6A6976';

const INTERACTION_STATE_COLORS = {
  pending: '#E49325',
  selected: '#006638',
} as const;

const DEFAULT_TYPE_ICONS: Record<string, string> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Issue: issueIconUrl,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Fault: faultIconUrl,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Comment: commentIconUrl,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Request: requestIconUrl,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Inquiry: inquiryIconUrl,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Solution: solutionIconUrl,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Remark: remarkIconUrl,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Clash: clashIconUrl,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface MarkerColorOptions {
  readonly priority?: string;
  readonly isSelected?: boolean;
  readonly isPending?: boolean;
}

/**
 * Store the API configuration (and optionally resolved icon content) at
 * module level so that {@link getMarkerColor} and {@link getMarkerTypeIcon}
 * can use it without callers needing to pass configuration explicitly.
 *
 * @param config - The {@link GetConfigurationResponse} from the Issues API.
 * @param resolvedIcons - Optional map of `iconId` → icon content (base64 or URL),
 *   built from the default-icons / list-icons API responses.
 */
export { configReady, iconsVersion };

export function setConfiguration(
  config: GetConfigurationResponse,
  resolvedIcons?: Map<string, string>
): void {
  const changed = configuration !== config;
  configuration = config;
  if (resolvedIcons) {
    resolvedIconsMap = resolvedIcons;
    setIconsVersion((v) => v + 1);
  }
  iconPathCache.clear();
  if (changed) {
    setConfigReady(true);
  }
}

/**
 * Reset all module-level configuration state to initial values.
 * Intended for test isolation — call in `beforeEach` / `afterEach`.
 */
export function resetConfiguration(): void {
  configuration = null;
  resolvedIconsMap = new Map();
  iconPathCache.clear();
  setConfigReady(false);
  setIconsVersion(0);
}

/**
 * Get the marker color for an issue based on its priority and interaction state.
 *
 * Resolution order:
 * 1. `isPending` → Warning Orange
 * 2. `isSelected` → Success Green
 * 3. API configuration color for the given priority (if {@link setConfiguration} was called)
 * 4. Hardcoded default for the priority
 * 5. Fallback gray
 */
export function getMarkerColor(options: MarkerColorOptions = {}): string {
  if (options.isPending) {
    return INTERACTION_STATE_COLORS.pending;
  }

  if (options.isSelected) {
    return INTERACTION_STATE_COLORS.selected;
  }

  const priority = options.priority;
  if (!priority) {
    return FALLBACK_PRIORITY_COLOR;
  }

  if (configuration?.priority) {
    const priorityConfig = configuration.priority.find(
      (item: ConfigurationDetails) => item.name === priority
    );
    if (priorityConfig?.color) {
      return priorityConfig.color;
    }
  }

  return DEFAULT_PRIORITY_COLORS[priority] ?? FALLBACK_PRIORITY_COLOR;
}

/**
 * Get the marker icon URL/content for an issue type.
 *
 * Resolution order:
 * 1. Custom icon (`custom:` prefix) from the stored icon map
 * 2. Bundled default SVG asset for the type
 * 3. Fallback to the BCF default icon
 */
export function getMarkerTypeIcon(type?: string): string {
  if (type && configuration?.type) {
    const typeConfig = configuration.type.find(
      (item: ConfigurationDetails) => item.name === type
    );
    if (typeConfig?.iconId?.startsWith('custom:')) {
      const resolved = resolvedIconsMap.get(typeConfig.iconId);
      if (resolved) {
        return resolved;
      }
    }
  }

  if (type && DEFAULT_TYPE_ICONS[type]) {
    return DEFAULT_TYPE_ICONS[type];
  }

  return bcfIconUrl;
}

/**
 * Check whether a marker type uses a custom (user-uploaded) icon.
 * Custom icons should be rendered as-is without compositing.
 */
export function isCustomMarkerIcon(markerType?: string): boolean {
  if (!markerType || !configuration?.type) {
    return false;
  }

  const typeConfig = configuration.type.find(
    (item: ConfigurationDetails) => item.name === markerType
  );

  return typeConfig?.iconId?.startsWith('custom:') === true;
}

// ---------------------------------------------------------------------------
// Composite icon generation
// ---------------------------------------------------------------------------

const DEFAULT_COMPOSITE_SIZE = 128;
const ICON_SCALE = 0.6;
const TWO = 2;
const BORDER_RATIO = 0.08;

export interface MarkerIconPathOptions {
  readonly priority?: string;
  readonly markerType?: string;
  readonly isSelected?: boolean;
  readonly isPending?: boolean;
}

/**
 * Load an image from a URL and return it as an {@link HTMLImageElement}.
 */
async function loadIconImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
}

/**
 * Generate a composite marker icon (white circle with colored border + original
 * icon overlay) and return it as a data URL suitable for {@link IconsPlugin}.
 *
 * Results are cached by the resolved color + icon URL combination. The cache
 * is invalidated when {@link setConfiguration} is called.
 *
 * Graceful degradation:
 * - No `markerType` → falls back to `bcf.svg`
 * - No `priority` → falls back to default color
 * - `isPending` / `isSelected` → overrides border color regardless
 * - Icon load failure → white circle with colored border (no icon)
 */
export async function getMarkerIconPath(
  options: MarkerIconPathOptions = {}
): Promise<string> {
  const color = getMarkerColor(options);
  const iconUrl = getMarkerTypeIcon(options.markerType);
  const cacheKey = `${color}:${iconUrl}`;

  const cached = iconPathCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const size = DEFAULT_COMPOSITE_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return iconUrl;
  }

  const borderWidth = size * BORDER_RATIO;
  const center = size / TWO;
  const radius = center - borderWidth / TWO;

  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * TWO);
  ctx.fill();

  ctx.strokeStyle = color;
  ctx.lineWidth = borderWidth;
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * TWO);
  ctx.stroke();

  try {
    const img = await loadIconImage(iconUrl);
    const iconSize = size * ICON_SCALE;
    const offset = (size - iconSize) / TWO;

    ctx.drawImage(img, offset, offset, iconSize, iconSize);
  } catch {
    // Icon failed to load — white circle with colored border is the fallback
    logger.error('[MarkerVisuals] Icon failed to load:', iconUrl);
  }

  const dataUrl = canvas.toDataURL('image/png');
  iconPathCache.set(cacheKey, dataUrl);
  return dataUrl;
}
